/**
 * RAM — landing surface.
 *
 * Search-first (per modules digest §RAM): FSR resolves a customer via the shared
 * `GlobalCustomerSearch` primitive; once the target is resolved, the action
 * catalogue lights up.
 *
 * Action selection → navigates to `/ram/wizard/:action` with target params.
 *
 * History: an earlier wave had this screen roll its own 4-field segregated
 * search (CIF / LastName / Acct / AcctType). That was a regression vs. the
 * legacy SPFx `PersonSearchBar` — see gap G-XM-01 in
 * `docs/audit/port-gaps/03-maintenance-family-gaps.md`. The shared primitive
 * ports the SSN / CIF / name auto-detect, parallel-query dedupe, kill-list
 * filter, and rich result cards.
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { Banner, GlobalCustomerSearch, Pill } from '@/components/primitives';
import type { CustomerSearchRow } from '@/components/primitives';
import { RAM_ACTIONS } from './types';
import type { RamActionCode } from './types';

interface ResolvedCif {
  cifId: string;
  name?: string;
}

export default function RamLanding(): JSX.Element {
  const navigate = useNavigate();
  const [search] = useSearchParams();

  // Deep-link preload: RLQ claim / direct URL with ?cifId= lights the action
  // catalogue immediately, bypassing the search. `?acctId=` / `?acctType=` are
  // carried through to the wizard.
  const preloadCif = search.get('cifId') ?? '';
  const preloadName = search.get('name') ?? '';
  const preloadAcct = search.get('acctId') ?? '';
  const preloadAcctType = search.get('acctType') ?? '';

  const [resolved, setResolved] = useState<ResolvedCif | null>(
    preloadCif ? { cifId: preloadCif, ...(preloadName ? { name: preloadName } : {}) } : null,
  );

  // Keep resolved in sync if the query param changes mid-session (Teams tab
  // deep-link re-navigation).
  useEffect(() => {
    if (preloadCif && resolved?.cifId !== preloadCif) {
      setResolved({ cifId: preloadCif, ...(preloadName ? { name: preloadName } : {}) });
    }
  }, [preloadCif, preloadName, resolved?.cifId]);

  const handleSelect = (row: CustomerSearchRow): void => {
    setResolved({ cifId: row.cifId, name: row.name });
  };

  const newSearch = (): void => setResolved(null);

  const queryParamBase = useMemo<string>(() => {
    if (!resolved) return '';
    const qp = new URLSearchParams({ cifId: resolved.cifId });
    if (resolved.name) qp.set('name', resolved.name);
    if (preloadAcct) qp.set('acctId', preloadAcct);
    if (preloadAcctType) qp.set('acctType', preloadAcctType);
    return qp.toString();
  }, [resolved, preloadAcct, preloadAcctType]);

  const openAction = (action: RamActionCode): void => {
    if (!resolved) return;
    navigate(`/ram/wizard/${action}?${queryParamBase}`);
  };

  return (
    <div className="ram-page">
      <header className="ram-page__header">
        <div className="ram-page__title-row">
          <h1 className="ram-page__title">Account maintenance</h1>
          <Pill variant="muted">Module RAM</Pill>
          {resolved && <Pill variant="info">CIF {resolved.cifId}</Pill>}
          {resolved?.name && <Pill variant="info">{resolved.name}</Pill>}
        </div>
        <p className="ram-page__subtitle">
          Search for the customer, then pick a maintenance action.
          Actions emit audit events and call CustMod / AcctMod as appropriate.
        </p>
      </header>

      {!resolved && (
        <section className="ram-search" aria-label="Search">
          <GlobalCustomerSearch onSelect={handleSelect} />
        </section>
      )}

      {resolved && (
        <section className="ram-search" aria-label="Selected customer">
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: 'var(--sp-5) var(--sp-6)',
              background: 'var(--surface-card)',
              border: '1px solid var(--surface-border)',
              borderRadius: 'var(--r-sm)',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-4)' }}>
              <strong>{resolved.name ?? `CIF ${resolved.cifId}`}</strong>
              <span style={{ color: 'var(--ink-500)', fontSize: 12, fontFamily: 'var(--font-mono)' }}>
                {resolved.cifId}
              </span>
            </div>
            <button
              type="button"
              className="btn btn-ghost"
              onClick={newSearch}
            >
              New search
            </button>
          </div>
        </section>
      )}

      <section aria-label="Actions">
        <h2
          style={{
            fontSize: 15,
            color: 'var(--pcb-blue-dark)',
            fontWeight: 600,
            margin: '0 0 var(--sp-5) 0',
          }}
        >
          Actions
        </h2>
        {!resolved && (
          <Banner tone="info">Select a customer above to enable actions.</Banner>
        )}
        {resolved && (
          <div className="ram-actions">
            {RAM_ACTIONS.map((a) => (
              <button
                key={a.code}
                type="button"
                className={`ram-action-card ${
                  a.pendingClarification ? 'ram-action-card--todo' : ''
                }`}
                onClick={() => openAction(a.code)}
              >
                <h3 className="ram-action-card__title">{a.title}</h3>
                <p className="ram-action-card__desc">{a.description}</p>
                {a.pendingClarification && (
                  <Pill variant="warn">Pending clarification</Pill>
                )}
              </button>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
